import type { ActivityCompletionMetrics } from "@/app/integration/contracts";
import { createLevelDefinitionSchema } from "@/domain/schemas";
import {
  createScenarioState,
  getScenarioAttemptMetrics,
  reduceScenario,
  scenarioPayloadSchema,
  type ScenarioPayload,
  type ScenarioState
} from "@/game/templates/scenario";

import {
  requireActivityType,
  requireHabitsAudioPath,
  requirePlayerMedia
} from "./binding-utils";
import type {
  HabitsPlayerLevel,
  SchoolScenarioAction,
  SchoolScenarioBinding,
  SchoolScenarioState
} from "./types";

const schoolScenarioLevelSchema = createLevelDefinitionSchema(
  scenarioPayloadSchema
);

function getMetrics(
  state: ScenarioState
): ActivityCompletionMetrics {
  const metrics = getScenarioAttemptMetrics(state);
  const completed = state.status === "completed";

  return {
    completed,
    score: Math.round(metrics.correctRate * 100),
    correctRate: metrics.correctRate,
    hintCount: metrics.hintCount,
    retryCount: metrics.retryCount
  };
}

function createState(): SchoolScenarioState {
  return {
    ...createScenarioState(),
    feedbackPlaybackRequest: 0
  };
}

function reduce(
  state: SchoolScenarioState,
  payload: ScenarioPayload,
  action: SchoolScenarioAction
): SchoolScenarioState {
  if (action.type === "replay") {
    return createState();
  }

  const nextState = reduceScenario(state, action, payload);
  if (nextState === state) {
    return state;
  }

  return {
    ...nextState,
    feedbackPlaybackRequest:
      nextState.outcome !== null && nextState.outcome !== state.outcome
        ? state.feedbackPlaybackRequest + 1
        : state.feedbackPlaybackRequest
  };
}

function createAdapter(
  level: HabitsPlayerLevel<ScenarioPayload>
): SchoolScenarioBinding["adapter"] {
  return {
    activityType: "school-scenarios",
    parsePayload: (value) => scenarioPayloadSchema.parse(value),
    createInitialState: (payload, context) => {
      void payload;
      void context;
      return createState();
    },
    reduce: (state, payload, action, context) => {
      void context;

      return reduce(state, payload, action);
    },
    getMetrics: (state, payload) => {
      void payload;
      return getMetrics(state);
    },
    toRenderModel: (state, payload) => {
      const metrics = getMetrics(state);
      const outcome = state.outcome;

      return {
        difficulty: level.difficulty,
        instruction: {
          audioPath: level.promptAudio,
          caption: level.instruction
        },
        levelId: level.id,
        sceneImagePath: level.assets[0],
        title: level.title,
        sceneNarration: {
          caption: payload.narration.text,
          audioPath: requireHabitsAudioPath(
            payload.narration.audio,
            level.id,
            "scenario narration"
          )
        },
        choices: payload.choices.map((choice) => ({
          audioPath: requireHabitsAudioPath(
            choice.audio,
            level.id,
            "scenario choice " + choice.id
          ),
          id: choice.id,
          selected: state.selectedChoiceId === choice.id,
          text: choice.text
        })),
        feedback:
          outcome === null
            ? null
            : {
                audioPath: requireHabitsAudioPath(
                  outcome.supportiveFeedback.audio,
                  level.id,
                  "scenario choice feedback " + outcome.choiceId
                ),
                kind: outcome.isBestChoice ? "success" : "retry",
                playbackRequest: state.feedbackPlaybackRequest,
                text: outcome.supportiveFeedback.text
              },
        followUp:
          outcome?.followUp === null || outcome?.followUp === undefined
            ? null
            : {
                audioPath: requireHabitsAudioPath(
                  outcome.followUp.audio,
                  level.id,
                  "scenario follow-up"
                ),
                playbackRequest: state.feedbackPlaybackRequest,
                text: outcome.followUp.text
              },
        phase: state.status === "completed" ? "result" : "play",
        result: state.status === "completed" ? metrics : null
      };
    }
  };
}

export function createSchoolScenarioBinding(
  sourceLevel: unknown
): SchoolScenarioBinding {
  const level = requirePlayerMedia(
    schoolScenarioLevelSchema.parse(sourceLevel)
  );
  requireActivityType(level, "school-scenarios");

  return {
    activityType: "school-scenarios",
    level,
    adapter: createAdapter(level)
  };
}
